import type { ChangeEvent } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import TextArea from "../../components/form/input/TextArea";
import { useEffect, useState } from "react";
import useGoBack from "../../hooks/useGoBack";
import UserService from "../../services/user.service";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import Input from "@/components/form/input/InputField";
import { useGetUserById, useUpdateUser } from "@/hooks/useUsers";
import { useTranslation } from "react-i18next";

export default function EditUser() {
    const { user_id } = useParams();
    const [userData, setUserData] = useState({
        full_name: "",
        email_address: "",
        phone_number: "",
    });
    const { t } = useTranslation();
    const [errors, setErrors] = useState({
        full_name: "",
        email_address: "",
    });

    const { data, isLoading: isFetching } = useGetUserById(user_id || "");
    const { mutate: updateUser, isLoading: isSubmitting } = useUpdateUser();
    const goBack = useGoBack();

    useEffect(() => {
        if (data?.status === 200 && data?.data?.user) {
            setUserData({
                full_name: data?.data?.user?.full_name,
                email_address: data?.data?.user?.email_address,
                phone_number: data?.data?.user?.phone_number,
            });
        }
    }, [data]);

    const validate = () => {
        const newErrors = {
            full_name: userData.full_name.trim() === "" ? t("fullNameRequired") : "",
            email_address: userData.email_address.trim() === "" ? t("emailRequired") : "",
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== "");
    };

    const handleInputChange = (field: "full_name" | "email_address" | "phone_number") =>
        (event: ChangeEvent<HTMLInputElement>) => {
            setUserData(prev => ({ ...prev, [field]: event.target.value }));
            setErrors(prev => ({ ...prev, [field]: "" }));
        };


    const handleEditDeck = async () => {
        if (!validate()) return;

        updateUser(
            { id: user_id as string, data: userData },
            {
                onSuccess: () => {
                    toast.success("User updated successfully!");
                    window.location.href = "/list-users";
                },
                onError: () => {
                    toast.error("Failed to update user. Please try again.");
                },
            }
        )
    };

    return (
        <div>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description={t("Edit User")}
            />
            <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
                <div className="space-y-6">
                    <ComponentCard title="">
                        <div className="space-y-6">
                            <div>
                                <Label>{t("Full Name")}</Label>
                                <Input
                                    type="text"
                                    value={userData.full_name}
                                    onChange={handleInputChange("full_name")}
                                    placeholder={t("Full Name")}
                                    disabled={isSubmitting}
                                />
                                {errors.full_name && (
                                    <p className="mt-1 text-sm text-red-600">{t(errors.full_name)}</p>
                                )}
                            </div>
                            <div>
                                <Label>{t("Email Address")}</Label>
                                <Input
                                    type="email"
                                    value={userData.email_address}
                                    onChange={handleInputChange("email_address")}
                                    placeholder={t("Email Address")}
                                    disabled={isSubmitting}
                                />
                                {errors.email_address && (
                                    <p className="mt-1 text-sm text-red-600">{t(errors.email_address)}</p>
                                )}
                            </div>
                            <div>
                                <Label>{t("Phone Number")}</Label>
                                <Input
                                    type="text"
                                    value={userData.phone_number}
                                    onChange={handleInputChange("phone_number")}
                                    placeholder={t("Phone Number")}
                                    disabled={isSubmitting}
                                />

                            </div>
                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={goBack}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                                    disabled={isSubmitting}
                                >
                                    {t("Cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    onClick={() => {
                                        const hasErrors = validate();
                                        if (hasErrors) {
                                            handleEditDeck()
                                        }
                                    }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t("Editing")}...
                                        </div>
                                    ) : (
                                        <>{t("Edit")}</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </ComponentCard>
                </div>
            </div>
        </div>
    );
}
